import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from '@kyruus/intl';
import type { PMCConfig } from '@kyruus/types';
import { Button } from '@biome-design-system/ui/Button';

import { configSelector } from 'Src/behaviors/configuration';
import { useUserLocation, useDemographicsLocation } from 'Src/hooks';

import { messages } from '../../messages';

export interface CurrentLocationButtonProps {
  /**
   * Current display location. When it changes we reset the browser-location
   * request so the user can "use current location" again after a new search.
   */
  displayLocation: string | undefined;
  /** Called with the resolved location when the user's location is available. */
  onUseCurrentLocation: (location: string, displayLocation: string) => void;
}

export const CurrentLocationButton = ({
  displayLocation,
  onUseCurrentLocation
}: CurrentLocationButtonProps) => {
  const config: PMCConfig = useSelector(configSelector);

  const [requestingBrowserLocation, setRequestingBrowserLocation] =
    useState(false);

  // gate behind the `requestingBrowserLocation` flag so that we only prompt the user
  // to allow giving us their location after they clicked on the CTA
  const { userLatLng, userHrfAddress, userLocationIsLoading } = useUserLocation(
    requestingBrowserLocation
  );

  // get patient zip from demographics (CCF or SMART on FHIR) if available
  const { patientZipSearchLocation, patientZip, patientZipPending } =
    useDemographicsLocation({
      config
    });

  useEffect(() => {
    // when we get a lat/lon after requesting browser location, send it up to the callback
    if (requestingBrowserLocation) {
      if (userLatLng && userHrfAddress) {
        onUseCurrentLocation(userLatLng, userHrfAddress);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLatLng, userHrfAddress, requestingBrowserLocation]);

  useEffect(() => {
    // when incoming displayLocation changes, that means a different search happened
    // and we're no longer using 'current' location. Resetting the state here allows
    // the user to 'use current location' multiple times in case they changed the query
    // from the original 'current location'
    setRequestingBrowserLocation(false);
  }, [displayLocation]);

  return (
    <Button
      data-testid="use-current-location"
      variant="outlined"
      size="small"
      sx={{ width: 'fit-content' }}
      disabled={patientZipPending || userLocationIsLoading}
      loading={patientZipPending || userLocationIsLoading}
      onClick={() => {
        // Patient zip from demographics (CCF or SMART on FHIR)
        if (patientZip) {
          onUseCurrentLocation(
            patientZipSearchLocation ?? patientZip,
            patientZip
          );
        } else {
          // browser geo
          setRequestingBrowserLocation(true);
        }
      }}
    >
      <FormattedMessage {...messages.useCurrentLocationLabel} />
    </Button>
  );
};
